import { useState, useEffect, useCallback } from 'react';
import { getApiUrl, type ActiveMind } from '../services/api';

export interface SignalAlert {
  id: string;
  signal: string;
  severity: 'info' | 'warning' | 'severe' | 'crisis';
  emotion_name: string;
  signal_value: number | null;
  status: 'active' | 'acknowledged' | 'resolved';
  fired_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
}

/** Alerts live in each mind's own D1 (both cortexes serve /alerts), so reads AND actions use `mind`. */
export function useAlerts(token: string, mind: ActiveMind) {
  const API_URL = getApiUrl(mind);
  const [alerts, setAlerts] = useState<SignalAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async (status?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (status) params.set('status', status);
      const res = await fetch(`${API_URL}/alerts?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch alerts: ${res.status}`);
      const data = await res.json();
      setAlerts(data.alerts ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  }, [token, API_URL]);

  const acknowledgeAlert = useCallback(async (id: string) => {
    try {
      const res = await fetch(`${API_URL}/alerts/${id}/acknowledge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to acknowledge alert: ${res.status}`);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'acknowledged' as const, acknowledged_at: new Date().toISOString() } : a));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to acknowledge alert');
    }
  }, [token, API_URL]);

  const resolveAlert = useCallback(async (id: string) => {
    try {
      const res = await fetch(`${API_URL}/alerts/${id}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to resolve alert: ${res.status}`);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'resolved' as const, resolved_at: new Date().toISOString() } : a));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resolve alert');
    }
  }, [token, API_URL]);

  // Initial fetch
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Poll every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchAlerts(), 30_000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  return { alerts, loading, error, fetchAlerts, acknowledgeAlert, resolveAlert };
}
